/**
 * Builds typed URLs for organization settings pages.
 *
 * 1. Keep every promoted organization settings tab in one enum.
 * 2. Build the route with the shared organization settings path.
 * 3. Preserve supported query parameters for return navigation and coding tools.
 *
 * New settings pages must add their tab here so links and route coverage use the same value.
 */
import urlcat from 'urlcat';

import { Routes } from 'src/constants/routes';

import { SearchParamKnownKeys } from 'src/hooks/useUrlSearchParams';

export enum OrganizationSettingsTab {
  SCHEDULING = 'scheduling',
  CANDIDATE_AVAILABILITY_REQUESTS = 'candidate-availability-requests',
  SELF_SCHEDULE_REQUESTS = 'self-schedule-requests',
  SCHEDULE_SETTINGS = 'schedule-settings',
  CALENDAR_EVENT_KEYWORDS = 'calendar-event-keywords',
  CANDIDATE_COMMUNICATIONS = 'candidate-communications',
  INTERNAL_COMMUNICATIONS = 'internal-communications',
  DEBRIEF_SETTINGS = 'debrief-settings',
  SCHEDULE_LOCATION = 'schedule-location',
  DEBRIEF_LOCATION = 'debrief-location',
  RESCHEDULING = 'rescheduling',
  INTERVIEW_PLAN_SYNC = 'interview-plan-sync',
  INTERVIEWERS = 'interviewers',
  INTERVIEWER_WORK_HOURS = 'interviewer-work-hours',
  INTERVIEWER_LOAD_LIMITS = 'interviewer-load-limits',
  INTERVIEWER_MEETING_TIME = 'interviewer-meeting-time',
  INTERVIEWER_FIELDS = 'interviewer-fields',
  INTERVIEWER_CUSTOM_ATTRIBUTES = 'interviewer-custom-attributes',
  INTERVIEWER_PORTAL = 'interviewer-portal',
  TEMPLATES = 'templates',
  KNOWLEDGE_BASES = 'knowledge-bases',
  CANDIDATE_PORTAL = 'candidate-portal',
  CANDIDATE_PORTAL_SETUP = 'candidate-portal-setup',
  CANDIDATE_PORTAL_BRANDING_THEMES = 'candidate-portal-branding-themes',
  CANDIDATE_PORTAL_PAGE_TEMPLATES = 'candidate-portal-page-templates',
  CANDIDATE_PORTAL_PORTALS = 'candidate-portal-portals',
  CANDIDATE_PORTAL_AUTOMATION = 'candidate-portal-automation',
  CANDIDATE_PORTAL_PULSE_SURVEY = 'candidate-portal-pulse-survey',
  NOTIFICATIONS = 'notifications',
  INTEGRATIONS = 'integrations',
  CODING_TOOL = 'coding-tool',
  INTEGRATION_GREENHOUSE = 'integration-greenhouse',
  INTEGRATION_LEVER = 'integration-lever',
  INTEGRATION_ASHBY = 'integration-ashby',
  INTEGRATION_WORKDAY = 'integration-workday',
  INTEGRATION_MICROSOFT = 'integration-microsoft',
  INTEGRATION_MICROSOFT_GCC_HIGH = 'integration-microsoft-gcc-high',
  INTEGRATION_MICROSOFT_SERVICE_USER = 'integration-microsoft-service-user',
  INTEGRATION_GOOGLE_WORKSPACE = 'integration-google-workspace',
  INTEGRATION_SMARTRECRUITERS = 'integration-smartrecruiters',
  INTEGRATION_GEM = 'integration-gem',
  INTEGRATION_SLACK = 'integration-slack',
  INTEGRATION_ZOOM = 'integration-zoom',
  MEMBERS = 'members',
  TASK = 'tasks',
  TASK_SETTINGS = 'task-settings',
  TASK_TAGS = 'task-tags',
  TASK_QUEUES = 'task-queues',
  TASK_RESCHEDULE_REASONS = 'task-reschedule-reasons',
  TASK_SEND_ON_BEHALF_OF_PERMISSIONS = 'task-send-on-behalf-of-permissions',
  PRIVACY = 'privacy',
  SETUP = 'setup',
  CANDIDATE_REQUESTS = 'candidate-requests',
  COMPANY_DAYS_OFF = 'company-days-off',
  GDPR_CCPA = 'gdpr-ccpa',
  SECRETS = 'secrets',
  DOWNLOADS = 'downloads',
  MEETING_ROOMS = 'meeting-rooms',
  AI = 'ai',
  SESSION_TIMEOUT = 'session-timeout',
  TRANSLATIONS = 'translations',
  PRIVACY_AUDIT_LOG = 'privacy-audit-log',
  ORG_ACTIVITY_LOG = 'org-activity-log',
  AUTHORIZED_APPS = 'authorized-apps',
  API_KEYS = 'api-keys',
  API_AUDIT_LOG = 'api-audit-log',
  MCP_AUDIT_LOG = 'mcp-audit-log',
}

export type OrganizationSettingsUrlType = `/settings/organization/${OrganizationSettingsTab}`;

type Props = {
  tab: OrganizationSettingsTab;
  returnTo?: string;
  /** Selects the coding tool for the CODING_TOOL tab (e.g. `HACKERRANK`); a query param. */
  tool?: string;
};

export const getOrganizationSettingUrls = ({ tab, returnTo, tool }: Props): OrganizationSettingsUrlType => {
  return urlcat('', Routes.OrganizationSettings, {
    tab,
    [SearchParamKnownKeys.RETURN_TO]: returnTo,
    [SearchParamKnownKeys.codingTool]: tool,
  }) as OrganizationSettingsUrlType;
};
